import {
  Box,
  Stack,
  Typography,
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  alpha,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

import { ReplyResponse } from '@/api/endpoints/community/posts/useGetReplies';
import { DotsVerticalIcon } from '@/assets/icons/DotsVerticalIcon';
import { HeartIcon } from '@/assets/icons/HeartIcon';
import { MessageChatCircleIcon } from '@/assets/icons/MessageChatCircleIcon';
import { useAuth } from '@/context/AuthProvider';

import { ReplyForm, ReplyFormData } from './ReplyForm';
import { ReplyThread } from './ReplyThread';

interface ReplyItemProps {
  reply: ReplyResponse;
  onReply: (data: ReplyFormData) => Promise<void>;
  onEdit?: (replyId: number, data: ReplyFormData) => Promise<void>;
  onDelete?: (replyId: number) => Promise<void>;
  onToggleLike?: (replyId: number) => Promise<void>;
  isUserMember?: boolean;
  isUserAdmin?: boolean;
  level?: number;
  maxLevel?: number;
}

export const ReplyItem = ({
  reply,
  onReply,
  onEdit,
  onDelete,
  onToggleLike,
  isUserMember = false,
  isUserAdmin = false,
  level = 0,
  maxLevel = 3,
}: ReplyItemProps): JSX.Element => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showNestedReplies, setShowNestedReplies] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const isAuthor = user?.id === reply.author.user.id;
  const canEdit = isAuthor || isUserAdmin;
  const canDelete = isAuthor || isUserAdmin;
  const canReply = isUserMember && level < maxLevel;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    // TODO: Implement edit functionality
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Are you sure you want to delete this reply?')) {
        onDelete(reply.id);
      }
    }
  };

  const handleReply = async (data: ReplyFormData) => {
    await onReply({
      ...data,
      parentId: reply.id,
    });
    setShowReplyForm(false);
  };

  const handleToggleLike = () => {
    if (onToggleLike) {
      onToggleLike(reply.id);
    }
  };

  const getAvatarProps = (name: string) => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    return {
      children: initials,
    };
  };

  return (
    <Box sx={{ mb: { xs: 0.5, sm: 1 } }}>
      <Stack direction="row" spacing={{ xs: 1, sm: 2 }}>
        {/* Avatar */}
        <Avatar
          {...getAvatarProps(reply.author.user.full_name || reply.author.user.email)}
          sx={{
            width: { xs: 24, sm: 28 },
            height: { xs: 24, sm: 28 },
            fontSize: { xs: '0.625rem', sm: '0.75rem' },
            bgcolor: 'grey.300',
            color: 'grey.700',
          }}
        />

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: { xs: 0.25, sm: 0.5 } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'text.primary',
                }}
              >
                {reply.author.user.full_name || reply.author.user.email}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.625rem', sm: '0.75rem' },
                  mt: { xs: -0.5, sm: -0.25 },
                  mb: 0,
                  lineHeight: 1,
                }}
              >
                {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
              </Typography>
            </Box>
            {(canEdit || canDelete) && (
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{
                  color: 'text.secondary',
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                <DotsVerticalIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />
              </IconButton>
            )}
          </Stack>

          {/* Content */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              mb: { xs: 0.5, sm: 1 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {reply.content}
          </Typography>

          {/* Actions */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1 }}
            sx={{ mb: { xs: 0.5, sm: 1 } }}
          >
            {/* Like Button - Reddit Style */}
            <Button
              startIcon={
                <HeartIcon
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                  style={{
                    fill: reply.is_liked ? '#f44336' : 'none',
                    stroke: reply.is_liked ? '#f44336' : 'currentColor',
                  }}
                />
              }
              size="small"
              onClick={handleToggleLike}
              disabled={!isUserMember}
              sx={{
                color: reply.is_liked ? 'error.main' : 'text.secondary',
                bgcolor: 'transparent',
                minWidth: 'auto',
                px: { xs: 0.75, sm: 1 },
                py: { xs: 0.25, sm: 0.25 },
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 400,
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 0.25,
                minHeight: { xs: '24px', sm: 'auto' },
                '&:hover': {
                  bgcolor: 'grey.100',
                },
                '&:active': {
                  bgcolor: 'grey.200',
                },
                '&:disabled': {
                  color: 'text.disabled',
                },
                '& .MuiButton-startIcon': {
                  margin: 0,
                },
              }}
            >
              {reply._count.likes}
            </Button>

            {/* Reply Button - Reddit Style */}
            {canReply && (
              <Button
                startIcon={
                  <MessageChatCircleIcon sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
                }
                size="small"
                onClick={() => setShowReplyForm(!showReplyForm)}
                sx={{
                  color: 'text.secondary',
                  bgcolor: 'transparent',
                  minWidth: 'auto',
                  px: { xs: 0.75, sm: 1 },
                  py: { xs: 0.25, sm: 0.25 },
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  gap: 0.25,
                  minHeight: { xs: '24px', sm: 'auto' },
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                  '&:active': {
                    bgcolor: 'grey.200',
                  },
                  '& .MuiButton-startIcon': {
                    margin: 0,
                  },
                }}
              >
                Reply
              </Button>
            )}

            {/* View Replies Button - Reddit Style */}
            {reply._count.replies > 0 && (
              <Button
                size="small"
                onClick={() => setShowNestedReplies(!showNestedReplies)}
                sx={{
                  color: 'text.secondary',
                  bgcolor: 'transparent',
                  minWidth: 'auto',
                  px: { xs: 0.75, sm: 1 },
                  py: { xs: 0.25, sm: 0.25 },
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  minHeight: { xs: '24px', sm: 'auto' },
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                  '&:active': {
                    bgcolor: 'grey.200',
                  },
                }}
              >
                {showNestedReplies ? 'Hide' : 'View'} {reply._count.replies}{' '}
                {reply._count.replies === 1 ? 'reply' : 'replies'}
              </Button>
            )}
          </Stack>

          {/* Reply Form */}
          <Collapse in={showReplyForm} timeout={300}>
            <Box sx={{ mb: { xs: 0.5, sm: 1 } }}>
              <ReplyForm
                onSubmit={handleReply}
                onCancel={() => setShowReplyForm(false)}
                placeholder={`Reply to ${reply.author.user.full_name}...`}
                compact
              />
            </Box>
          </Collapse>

          {/* Nested Replies */}
          {reply.nested_replies && reply.nested_replies.length > 0 && (
            <Collapse in={showNestedReplies} timeout={300}>
              <Box
                sx={{
                  ml: level > 0 ? { xs: 1, sm: 2 } : { xs: 1.5, sm: 3 },
                  pl: { xs: 1, sm: 2 },
                  borderLeft: '2px solid',
                  borderColor: 'secondary.200',
                }}
              >
                <ReplyThread
                  replies={reply.nested_replies}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleLike={onToggleLike}
                  isUserMember={isUserMember}
                  isUserAdmin={isUserAdmin}
                  level={level + 1}
                  maxLevel={maxLevel}
                />
              </Box>
            </Collapse>
          )}
        </Box>
      </Stack>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 120,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
            },
          },
        }}
      >
        {canEdit && (
          <MenuItem onClick={handleEdit} sx={{ fontSize: '0.875rem' }}>
            Edit Reply
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={handleDelete}
            sx={{
              fontSize: '0.875rem',
              color: 'error.main',
              '&:hover': {
                bgcolor: alpha('#f44336', 0.08),
              },
            }}
          >
            Delete Reply
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
